import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Clock,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

const BlumHistory = () => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadOperations();
  }, [page]);

  const loadOperations = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/blum/operations`, {
        params: { limit, offset: page * limit }
      });
      
      if (response.data.success) {
        setOperations(response.data.operations || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error loading operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num, decimals = 4) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(decimals);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Confirmed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
            {status}
          </Badge>
        );
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Card className="bg-white/10 backdrop-blur-sm border-white/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="h-5 w-5" />
              История операций
            </CardTitle>
            <CardDescription className="text-purple-200">
              Все операции покупки и продажи на BLUM DEX
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadOperations}
            disabled={loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && operations.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : operations.length === 0 ? (
          <div className="text-center py-12 text-purple-200">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>История операций пуста</p>
            <p className="text-sm opacity-75">Выполните первую сделку на BLUM DEX</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-purple-200">Время</TableHead>
                    <TableHead className="text-purple-200">Тип</TableHead>
                    <TableHead className="text-purple-200">Токен</TableHead>
                    <TableHead className="text-purple-200">Сумма</TableHead>
                    <TableHead className="text-purple-200">Цена</TableHead>
                    <TableHead className="text-purple-200">Статус</TableHead>
                    <TableHead className="text-purple-200">TX</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {operations.map((op) => (
                    <TableRow key={op.id} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-white text-sm">
                        {formatDate(op.created_at)}
                      </TableCell>
                      <TableCell>
                        {op.operation_type === 'buy' ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Buy
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Sell
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {op.token_symbol || op.token_address?.slice(0, 8) + '...'}
                      </TableCell>
                      <TableCell className="text-white">
                        <div>{formatNumber(op.token_amount)} tokens</div>
                        <div className="text-purple-300 text-xs">
                          {formatNumber(op.ton_amount, 2)} TON
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        {op.price_ton?.toFixed(9)} TON
                        <div className="text-purple-300 text-xs">
                          ${op.price_usd?.toFixed(6)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(op.status)}
                      </TableCell>
                      <TableCell>
                        {op.tonviewer_url || op.tx_hash ? (
                          <a 
                            href={op.tonviewer_url || `https://tonviewer.com/transaction/${op.tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                            title={op.tx_hash}
                          >
                            <ExternalLink className="h-4 w-4" />
                            <span className="text-xs hidden sm:inline">
                              {op.tx_hash?.slice(0, 8)}...
                            </span>
                          </a>
                        ) : (
                          <span className="text-purple-500">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <div className="text-purple-300 text-sm">
                  Показано {page * limit + 1} - {Math.min((page + 1) * limit, total)} из {total}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="bg-white/5 border-white/20 text-purple-200 hover:bg-white/10"
                  >
                    Назад
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="bg-white/5 border-white/20 text-purple-200 hover:bg-white/10"
                  >
                    Далее
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BlumHistory;


